import { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Plus, Trash2, Loader2, Pencil, X, Save } from "lucide-react";

const empty = { symbol: "", name: "", ratio: "", exercise_price: "", cum_date: "", ex_date: "", trading_start: "", trading_end: "", note: "" };
const fmt = (s) => { if (!s) return "-"; try { const d = new Date(s + "T00:00:00"); return d.toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }); } catch { return s; } };

function Form({ initial, onDone, onCancel }) {
  const [f, setF] = useState({ ...empty, ...initial, exercise_price: initial?.exercise_price ?? "" });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const inp = "w-full h-9 rounded-lg bg-[var(--surface-base)] border border-[var(--border)] px-2.5 text-sm outline-none focus:border-[var(--accent)]/50";

  const save = async () => {
    if (!f.symbol.trim()) { toast.error("Kode saham wajib diisi."); return; }
    setSaving(true);
    try {
      const payload = { ...f, symbol: f.symbol.toUpperCase(), exercise_price: f.exercise_price ? Number(f.exercise_price) : null };
      if (initial?.id) await api.put(`/admin/rights-issues/${initial.id}`, payload);
      else await api.post("/admin/rights-issues", payload);
      toast.success("Rights issue disimpan.");
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan."); }
    finally { setSaving(false); }
  };

  return (
    <div className="card p-4 mb-4 border-[var(--accent)]/30" data-testid="rights-form">
      <div className="flex items-center justify-between mb-3">
        <p className="font-heading font-bold text-sm">{initial?.id ? "Edit" : "Tambah"} Rights Issue</p>
        {onCancel && <button onClick={onCancel} className="p-1 rounded hover:bg-[var(--surface-hover)] text-[var(--text-secondary)]"><X size={15} /></button>}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <label className="text-xs"><span className="text-[var(--text-secondary)] block mb-1">Kode</span><input data-testid="ri-symbol" value={f.symbol} onChange={(e) => set("symbol", e.target.value)} placeholder="BBRI" className={inp} /></label>
        <label className="text-xs sm:col-span-2"><span className="text-[var(--text-secondary)] block mb-1">Nama (opsional)</span><input data-testid="ri-name" value={f.name} onChange={(e) => set("name", e.target.value)} className={inp} /></label>
        <label className="text-xs"><span className="text-[var(--text-secondary)] block mb-1">Rasio</span><input data-testid="ri-ratio" value={f.ratio} onChange={(e) => set("ratio", e.target.value)} placeholder="2 : 1" className={inp} /></label>
        <label className="text-xs"><span className="text-[var(--text-secondary)] block mb-1">Harga Tebus (Rp)</span><input data-testid="ri-price" type="number" value={f.exercise_price} onChange={(e) => set("exercise_price", e.target.value)} className={inp} /></label>
        <label className="text-xs"><span className="text-[var(--text-secondary)] block mb-1">Cum-Date</span><input data-testid="ri-cum" type="date" value={f.cum_date} onChange={(e) => set("cum_date", e.target.value)} className={inp} /></label>
        <label className="text-xs"><span className="text-[var(--text-secondary)] block mb-1">Ex-Date</span><input data-testid="ri-ex" type="date" value={f.ex_date} onChange={(e) => set("ex_date", e.target.value)} className={inp} /></label>
        <label className="text-xs"><span className="text-[var(--text-secondary)] block mb-1">Mulai HMETD</span><input data-testid="ri-start" type="date" value={f.trading_start} onChange={(e) => set("trading_start", e.target.value)} className={inp} /></label>
        <label className="text-xs"><span className="text-[var(--text-secondary)] block mb-1">Akhir Penebusan</span><input data-testid="ri-end" type="date" value={f.trading_end} onChange={(e) => set("trading_end", e.target.value)} className={inp} /></label>
        <label className="text-xs sm:col-span-3"><span className="text-[var(--text-secondary)] block mb-1">Catatan</span><input data-testid="ri-note" value={f.note} onChange={(e) => set("note", e.target.value)} className={inp} /></label>
      </div>
      <button onClick={save} disabled={saving} data-testid="ri-save" className="mt-3 h-9 px-4 rounded-lg bg-[var(--accent)] text-[#07090E] font-semibold inline-flex items-center gap-1.5 disabled:opacity-60">
        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan
      </button>
    </div>
  );
}

export default function AdminRightsIssue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/rights-issues").then((r) => setItems(r.data.items || [])).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (id) => {
    if (!window.confirm("Hapus jadwal rights issue ini?")) return;
    try { await api.delete(`/admin/rights-issues/${id}`); toast.success("Dihapus."); load(); }
    catch { toast.error("Gagal menghapus."); }
  };

  return (
    <div data-testid="admin-rights">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex items-center gap-2 text-sm text-[var(--text-secondary)]"><TrendingUp size={16} className="text-accent" /> <b className="text-[var(--text-primary)]">{items.length}</b> jadwal rights issue</div>
        {!showForm && !editing && <button onClick={() => setShowForm(true)} data-testid="ri-add-btn" className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-[var(--accent)] text-[#07090E] text-sm font-semibold"><Plus size={15} /> Tambah</button>}
      </div>

      <p className="text-[11px] text-[var(--text-muted)] mb-3">Sumber data resmi: keterbukaan informasi BEI / KSEI (web.ksei.co.id → Corporate Action → Rights Distribution) atau idnfinancials. Masukkan tanggal secara akurat.</p>

      {showForm && <Form onDone={() => { setShowForm(false); load(); }} onCancel={() => setShowForm(false)} />}
      {editing && <Form initial={editing} onDone={() => { setEditing(null); load(); }} onCancel={() => setEditing(null)} />}

      {loading ? (
        <div className="card p-12 flex items-center justify-center"><Loader2 className="animate-spin text-accent" /></div>
      ) : items.length === 0 ? (
        <div className="card p-10 text-center text-[var(--text-muted)]">Belum ada jadwal rights issue.</div>
      ) : (
        <div className="space-y-2">
          {items.map((e) => (
            <div key={e.id} className="card p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`ri-row-${e.symbol}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold text-accent">{e.symbol}</span>
                  <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-[var(--surface-hover)] text-[var(--text-secondary)]">{e.status}</span>
                  {e.ratio && <span className="text-[10px] text-[var(--text-muted)]">Rasio {e.ratio}</span>}
                  {e.exercise_price != null && <span className="text-[10px] text-[var(--text-muted)]">Tebus Rp {Number(e.exercise_price).toLocaleString("id-ID")}</span>}
                </div>
                <p className="text-[11px] text-[var(--text-muted)] mt-0.5">Cum {fmt(e.cum_date)} · Ex {fmt(e.ex_date)} · HMETD {fmt(e.trading_start)}–{fmt(e.trading_end)}</p>
              </div>
              <div className="flex gap-1.5 shrink-0">
                <button onClick={() => { setEditing(e); setShowForm(false); }} data-testid={`ri-edit-${e.symbol}`} className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-[var(--surface-hover)] border border-[var(--border)] text-xs font-semibold hover:text-accent"><Pencil size={13} /> Edit</button>
                <button onClick={() => del(e.id)} data-testid={`ri-del-${e.symbol}`} className="p-2 rounded-lg hover:bg-[var(--bear)]/15 text-[var(--text-muted)] hover:text-bear"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
